import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import {
  billComponents,
  categoryColors,
  applyScenarios,
  scenarioDescriptions,
  getAggregatedByCategory,
  getCategoryDisplayName
} from './data2030';
import './Forecast2030.css';

const Forecast2030 = () => {
  const [viewMode, setViewMode] = useState('total'); // 'total' or 'delta' - default to total
  const [activeComponent, setActiveComponent] = useState(null);
  const [activeCategory, setActiveCategory] = useState(null); // Track which category is being hovered
  const [activeInfo, setActiveInfo] = useState(null); // Can be component or scenario
  const [lockedComponent, setLockedComponent] = useState(null); // Track clicked/locked component
  const [lockedCategory, setLockedCategory] = useState(null); // Track clicked/locked category
  const [scenarioToggles, setScenarioToggles] = useState({
    removeVAT: false,
    moveECOToTax: false,
    reindexROandFiT: false
  });
  const [chartData, setChartData] = useState(billComponents);
  const [email, setEmail] = useState('');
  const [showSolutionsModal, setShowSolutionsModal] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [showWholesaleSlider, setShowWholesaleSlider] = useState(false);
  const [wholesalePrice, setWholesalePrice] = useState(70);
  const [showBenchmarkInfo, setShowBenchmarkInfo] = useState(false);

  // Track if we just clicked a bar (to prevent container click from firing)
  const justClickedBarRef = React.useRef(false);

  // Set page title and obfuscated email
  useEffect(() => {
    document.title = '2030 Electricity Bill Estimate';
    // Obfuscate email by building from parts
    const user = 'ben';
    const domain = 'benjames';
    const tld = 'io';
    setEmail(`${user}@${domain}.${tld}`);
  }, []);

  // Track window width for responsive label wrapping
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Recalculate chart data when scenarios or wholesale price change
  useEffect(() => {
    let adjustedData = applyScenarios(billComponents, scenarioToggles);

    // Always apply wholesale price adjustments
    const LINE_LOSS = 0.093;
    const DOMESTIC_DEMAND_CAPTURE_RATE = 0.065;
    const PRICE_CAP_ADJUSTMENT_AND_HEDGING = 1.066; // 6.6% adjustment for price cap and hedging
    const INFLATION_FACTOR = Math.pow(1.02, 5); // 2% compounded for 5 years (2025 to 2030)

    // Calculate new wholesale price in £2025 for the chart
    const newWholesalePrice = 3.1 * (1 + LINE_LOSS) * (1 + DOMESTIC_DEMAND_CAPTURE_RATE) * wholesalePrice * PRICE_CAP_ADJUSTMENT_AND_HEDGING;

    // Convert wholesale price from £2025 to £2030 for CfD calculations
    const wholesalePrice2030 = wholesalePrice * INFLATION_FACTOR;

    // CfD technology data (all prices in £2030)
    const cfdTechnologies = [
      { name: 'offshore wind', blendedPrice: 102.89, volume: 83.2, captureRate: 0.77 },
      { name: 'onshore wind', blendedPrice: 82.76, volume: 13.4, captureRate: 0.77 },
      { name: 'solar', blendedPrice: 76.11, volume: 7.2, captureRate: 0.81 },
      { name: 'biomass', blendedPrice: 173.76, volume: 8.2, captureRate: 1.10 },
      { name: 'hinkley', blendedPrice: 140.22, volume: 21.7, captureRate: 1.00 },
      { name: 'other', blendedPrice: 235.06, volume: 0.5, captureRate: 1.00 }
    ];

    // Calculate total CfD cost in millions (in £2030)
    let totalCfDCostMillions2030 = 0;
    cfdTechnologies.forEach(tech => {
      // Cost (£m) = volume_TWh * (blended_price - wholesale_price_2030 * capture_rate)
      const costMillions = tech.volume * (tech.blendedPrice - wholesalePrice2030 * tech.captureRate);
      totalCfDCostMillions2030 += costMillions;
    });

    // Convert total CfD cost back to £2025
    const totalCfDCostMillions2025 = totalCfDCostMillions2030 / INFLATION_FACTOR;

    // Convert to £ per household (3.1 MWh per household, with line losses)
    // Total cost in £m needs to be distributed across households
    // Chargeable demand = 270 TWh
    const CHARGEABLE_DEMAND = 270; // TWh
    const newCfDPrice = totalCfDCostMillions2025 * (3.1 * (1 + LINE_LOSS) / CHARGEABLE_DEMAND);

    // Update wholesale and CfD values in 2030
    adjustedData = {
      ...adjustedData,
      wholesale: {
        ...adjustedData.wholesale,
        value2030: newWholesalePrice
      },
      contractsForDifference: {
        ...adjustedData.contractsForDifference,
        value2030: newCfDPrice
      }
    };

    // Always recalculate VAT (5% of everything else) - but only if VAT is not removed
    if (!scenarioToggles.removeVAT) {
      let subtotal2030 = 0;
      Object.keys(adjustedData).forEach(key => {
        if (key !== 'vat') {
          subtotal2030 += adjustedData[key].value2030;
        }
      });
      const newVAT = subtotal2030 * 0.05;

      adjustedData = {
        ...adjustedData,
        vat: {
          ...adjustedData.vat,
          value2030: newVAT
        }
      };
    }

    setChartData(adjustedData);
  }, [scenarioToggles, wholesalePrice]);

  // Handle scenario toggle
  const handleScenarioToggle = (scenarioKey) => {
    setScenarioToggles(prev => ({
      ...prev,
      [scenarioKey]: !prev[scenarioKey]
    }));
  };

  // Handle category hover (for split-out effect)
  const handleCategoryHover = (categoryId) => {
    // If something is locked, don't change on hover
    if (lockedComponent || lockedCategory) return;

    setActiveCategory(categoryId);
    if (categoryId) {
      const categoryName = getCategoryDisplayName(categoryId);
      setActiveInfo({
        type: 'category',
        content: `<p>Hover over individual components to see details.</p>`,
        title: categoryName
      });
    } else {
      setActiveInfo(null);
    }
  };

  // Handle component hover (when split out)
  const handleComponentHover = (componentId, categoryId) => {
    // If something is locked, don't change on hover
    if (lockedComponent || lockedCategory) return;

    setActiveComponent(componentId);
    // When hovering a component, also set the active category to ensure split-out in both bars
    if (categoryId && categoryId !== activeCategory) {
      setActiveCategory(categoryId);
    }
    if (componentId && chartData[componentId]) {
      const component = chartData[componentId];

      // Always show both 2025 and 2030 values
      const price2025 = Math.round(component.value2025);
      const price2030 = Math.round(component.value2030);

      setActiveInfo({
        type: 'component',
        content: component.description,
        title: component.name,
        link: component.link || null,
        price2025: price2025,
        price2030: price2030
      });
    } else if (categoryId) {
      // Show category info if no specific component
      handleCategoryHover(categoryId);
    } else {
      setActiveInfo(null);
    }
  };

  // Handle component click (lock selection)
  const handleComponentClick = (componentId, categoryId) => {
    // If clicking the same component, unlock it
    if (lockedComponent === componentId) {
      setLockedComponent(null);
      setLockedCategory(null);
      setActiveComponent(null);
      setActiveCategory(null);
      setActiveInfo(null);
    } else {
      // Lock this component
      setLockedComponent(componentId);
      setLockedCategory(categoryId);
      setActiveComponent(componentId);
      setActiveCategory(categoryId);

      // Update info
      const component = chartData[componentId];
      const price2025 = Math.round(component.value2025);
      const price2030 = Math.round(component.value2030);

      setActiveInfo({
        type: 'component',
        content: component.description,
        title: component.name,
        link: component.link || null,
        price2025: price2025,
        price2030: price2030
      });
    }
  };

  // Handle scenario hover
  const handleScenarioHover = (scenarioKey) => {
    if (scenarioKey) {
      setActiveInfo({
        type: 'scenario',
        content: scenarioDescriptions[scenarioKey],
        title: null
      });
    } else if (!activeComponent) {
      setActiveInfo(null);
    }
  };

  // Prepare data for charts with split-out support
  const aggregatedData = getAggregatedByCategory(chartData);

  // Convert to format for stacked bar chart - Total View
  const prepareTotalChartData = () => {
    const todayBar = { name: '2025' };
    const forecast2030Bar = { name: '2030' };

    if (activeCategory) {
      // Show split-out view: other categories aggregated, active category split
      Object.entries(aggregatedData).forEach(([categoryId, categoryData]) => {
        if (categoryId === activeCategory) {
          // Split out this category into individual components
          categoryData.componentIds.forEach(componentId => {
            const component = chartData[componentId];
            todayBar[componentId] = component.value2025;
            forecast2030Bar[componentId] = component.value2030;
          });
        } else {
          // Show as aggregated category
          todayBar[`_category_${categoryId}`] = categoryData.value2025;
          forecast2030Bar[`_category_${categoryId}`] = categoryData.value2030;
        }
      });
    } else {
      // Show collapsed view: all categories aggregated
      Object.entries(aggregatedData).forEach(([categoryId, categoryData]) => {
        todayBar[`_category_${categoryId}`] = categoryData.value2025;
        forecast2030Bar[`_category_${categoryId}`] = categoryData.value2030;
      });
    }

    return [todayBar, forecast2030Bar];
  };

  const prepareDeltaChartData = () => {
    const increasesBar = { name: 'Increase' };
    const decreasesBar = { name: 'Decrease' };
    const netChangeBar = { name: 'Net Change' };

    let totalIncrease = 0;
    let totalDecrease = 0;

    // Define consistent category order
    const categoryOrder = ['wholesale', 'network', 'renewables', 'misc', 'newLevies'];

    // Always show full component breakdown in Delta view
    // First pass: add all increases by category
    categoryOrder.forEach(categoryId => {
      const categoryData = aggregatedData[categoryId];
      if (!categoryData) return;

      categoryData.componentIds.forEach(componentId => {
        const component = chartData[componentId];
        const componentDelta = component.value2030 - component.value2025;

        if (componentDelta > 0) {
          increasesBar[componentId] = componentDelta;
          totalIncrease += componentDelta;
        }
      });
    });

    // Second pass: add all decreases in same category order
    categoryOrder.forEach(categoryId => {
      const categoryData = aggregatedData[categoryId];
      if (!categoryData) return;

      categoryData.componentIds.forEach(componentId => {
        const component = chartData[componentId];
        const componentDelta = component.value2030 - component.value2025;

        if (componentDelta < 0) {
          decreasesBar[componentId] = Math.abs(componentDelta);
          totalDecrease += Math.abs(componentDelta);
        }
      });
    });

    // Add spacer for decreases bar alignment
    const spacerHeight = Math.max(0, totalIncrease - totalDecrease);
    if (spacerHeight > 0) {
      decreasesBar['_decreaseSpacer'] = spacerHeight;
    }

    // Net change bar
    const netValue = totalIncrease - totalDecrease;
    if (netValue >= 0) {
      netChangeBar['_net_increase'] = netValue;
    } else {
      netChangeBar['_net_decrease'] = Math.abs(netValue);
    }

    return [increasesBar, decreasesBar, netChangeBar];
  };

  const chartDataForDisplay = viewMode === 'total' ? prepareTotalChartData() : prepareDeltaChartData();

  // Calculate Y-axis domain for delta view
  const getYAxisDomain = () => {
    if (viewMode !== 'delta') {
      return [0, 'auto'];
    }

    // Find the Increase bar and calculate its total
    const increaseBar = chartDataForDisplay.find(bar => bar.name === 'Increase');
    if (!increaseBar) {
      return [0, 300];
    }

    let increaseTotal = 0;
    Object.entries(increaseBar).forEach(([key, val]) => {
      if (key !== 'name') {
        increaseTotal += val || 0;
      }
    });

    // Use 300 if total <= 300, otherwise use 400
    return increaseTotal > 300 ? [0, 400] : [0, 300];
  };

  // Get all unique keys from the chart data (includes categories and components)
  const getAllKeys = () => {
    const keys = new Set();

    chartDataForDisplay.forEach(bar => {
      Object.keys(bar).forEach(key => {
        if (key !== 'name') keys.add(key);
      });
    });

    // Ensure spacer comes first (bottom of stack), then categories/components, then net change bars
    const keysArray = Array.from(keys);
    const spacer = keysArray.filter(k => k === '_decreaseSpacer');
    const netBars = keysArray.filter(k => k === '_net_increase' || k === '_net_decrease');
    const regular = keysArray.filter(k => k !== '_decreaseSpacer' && k !== '_net_increase' && k !== '_net_decrease');

    return [...spacer, ...regular, ...netBars];
  };

  const allKeys = getAllKeys();

  return (
    <div className="forecast2030-container">
      <div className="forecast2030-content">
        {/* Chart Pane */}
        <div className="chart-pane">
          <div className="chart-header">
            <h1>2030 GB Electricity Bill Estimate</h1>
            <p className="chart-subtitle">
              Real £2025, benchmark 3100 kWh household
              <span
                className="benchmark-info-icon"
                onMouseEnter={() => setShowBenchmarkInfo(true)}
                onMouseLeave={() => setShowBenchmarkInfo(false)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </span>
              {showBenchmarkInfo && (
                <span className="benchmark-info-modal">
                  The OFGEM Typical Domestic Consumption Value is currently 2700 kWh for a profile class 1 household, and 3900 kWh for a profile class 2 household. Around 82% of households are class 1.
                <br/><br/>
                This analysis uses 3100 kWh as a benchmark since it is close to the weighted average of these, with a little added for electrification. It also enables direct comparison with OFGEM's previous price caps, which are calculated for a nominal 3100 kWh household.
                </span>
              )}
            </p>

            <div className="view-toggle">
              <button
                className={viewMode === 'total' ? 'active' : ''}
                onClick={() => setViewMode('total')}
              >
                Total
              </button>
              <button
                className={viewMode === 'delta' ? 'active' : ''}
                onClick={() => setViewMode('delta')}
              >
                Delta
              </button>
            </div>

            {viewMode === 'delta' && (
              <p className="chart-view-subtitle">Change from 2025 to 2030</p>
            )}
          </div>

          <div
            className="chart-container"
            onMouseLeave={() => {
              // When leaving container, only clear if nothing is locked
              if (!lockedComponent && !lockedCategory) {
                setActiveCategory(null);
                setActiveComponent(null);
                setActiveInfo(null);
              }
            }}
            onClick={(e) => {
              // If we just clicked a bar, ignore this event
              if (justClickedBarRef.current) {
                justClickedBarRef.current = false;
                return;
              }

              // Click on background (not a bar) clears lock
              if (e.target === e.currentTarget || e.target.closest('.recharts-surface')) {
                setLockedComponent(null);
                setLockedCategory(null);
                setActiveComponent(null);
                setActiveCategory(null);
                setActiveInfo(null);
              }
            }}
          >
            <ResponsiveContainer width="100%" height={500}>
              <BarChart
                data={chartDataForDisplay}
                margin={{
                  top: 5,
                  right: windowWidth < 768 ? -15 : 0,
                  left: windowWidth < 768 ? -15 : 0,
                  bottom: 45
                }}
                barGap={15}
                barCategoryGap={windowWidth >= 1024 && windowWidth < 1400
                  ? (viewMode === 'total' ? "10%" : "5%")
                  : (viewMode === 'total' ? "20%" : "8%")}
              >
                <XAxis
                  dataKey="name"
                  stroke="#ccc"
                  tick={(props) => {
                    const { x, y, payload } = props;
                    const barName = payload.value;

                    // Calculate value based on current chart data
                    const bar = chartDataForDisplay.find(b => b.name === barName);
                    let value = 0;

                    if (bar) {
                      Object.entries(bar).forEach(([key, val]) => {
                        if (key !== 'name' && key !== '_decreaseSpacer') {
                          value += val || 0;
                        }
                      });
                    }

                    // Check if this is a Delta view bar (Increase/Decrease/Net Change)
                    const isDeltaBar = barName === 'Increase' || barName === 'Decrease' || barName === 'Net Change';

                    if (isDeltaBar) {
                      // Delta view: show "INCREASE/DECREASE/NET CHANGE", "+/-£XXX"
                      return (
                        <g transform={`translate(${x},${y})`}>
                          <text
                            x={0}
                            y={0}
                            dy={20}
                            textAnchor="middle"
                            fill="#fff"
                            fontSize="15"
                            fontWeight="500"
                            style={{ textTransform: 'uppercase' }}
                          >
                            {barName}
                          </text>
                          <text
                            x={0}
                            y={0}
                            dy={36}
                            textAnchor="middle"
                            fill="#bbb"
                            fontSize="18"
                            fontWeight="600"
                          >
                            {barName === 'Increase' ? '+' : ''}
                            {barName === 'Decrease' ? '-' : ''}
                            {barName === 'Net Change' && bar && bar._net_increase ? '+' : ''}
                            {barName === 'Net Change' && bar && bar._net_decrease ? '-' : ''}
                            £{Math.round(Math.abs(value))}
                          </text>
                        </g>
                      );
                    }

                    // Total view: single line label with value
                    return (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={16}
                          textAnchor="middle"
                          fill="#bbb"
                          fontSize="18"
                          fontWeight="500"
                          style={{ textTransform: 'uppercase' }}
                        >
                          {barName}
                        </text>
                        <text
                          x={0}
                          y={0}
                          dy={36}
                          textAnchor="middle"
                          fill="#fff"
                          fontSize="18"
                          fontWeight="600"
                        >
                          £{Math.round(Math.abs(value))}
                        </text>
                      </g>
                    );
                  }}
                />
                <YAxis
                  stroke="#ccc"
                  tick={{ fill: '#ccc', fontSize: windowWidth < 768 ? 12 : 14 }}
                  tickFormatter={(value) => `£${value}`}
                  domain={getYAxisDomain()}
                />

                {/* Render bars for each key (category or component) */}
                {allKeys.map((key) => {
                  // Determine if this is a category or component
                  const isCategory = key.startsWith('_category_');
                  const isSpacer = key === '_decreaseSpacer';
                  const isNetIncrease = key === '_net_increase';
                  const isNetDecrease = key === '_net_decrease';
                  const isNetChange = isNetIncrease || isNetDecrease;
                  const isSpecial = isNetChange || isSpacer;

                  // Extract IDs
                  const categoryId = isCategory ? key.replace('_category_', '') : null;
                  const componentId = !isCategory && !isSpecial ? key : null;
                  const actualCategoryId = componentId ? chartData[componentId]?.categoryId : categoryId;

                  const renderLabel = (props) => {
                    const { x, y, width, height, value } = props;

                    // Don't show label for spacer
                    if (isSpacer) return null;

                    // Show label if segment is tall enough (at least 20px) and value exists
                    if (height < 20 || !value) return null;

                    let displayName;
                    if (isNetChange) {
                      displayName = isNetIncrease ? 'Net increase' : 'Net decrease';
                    } else if (isCategory) {
                      displayName = getCategoryDisplayName(categoryId);
                    } else {
                      displayName = chartData[componentId].name;
                    }

                    // Use smaller font for smaller segments or long labels
                    const isLongLabel = displayName.length > 20;
                    const fontSize = height < 35 || isLongLabel ? 11 : 13;

                    // Use black text for all except wholesale (orange) category
                    // Dim wholesale text when not highlighted
                    let textColor = actualCategoryId === 'wholesale' ? '#fff' : '#000';
                    if (actualCategoryId === 'wholesale' && hasActiveState && !isActive) {
                      textColor = 'rgba(255, 255, 255, 0.4)';
                    }

                    // Simple word wrap: split on spaces and stack if screen is narrow
                    const words = displayName.split(' ');

                    // On very narrow screens (<600px), hide long labels that would overflow
                    if (windowWidth < 600 && displayName.length > 15) {
                      return null;
                    }

                    // Wrap when window is narrow (mobile/tablet/laptop) and we have multi-word labels with enough height
                    // Use lower height threshold for 3+ word labels (like "Contracts for Difference")
                    const isVeryLongLabel = words.length >= 3;
                    const minHeightForWrap = isVeryLongLabel ? 25 : 30;
                    const widthThreshold = isVeryLongLabel ? 1400 : 1100;
                    const shouldWrap = windowWidth < widthThreshold && words.length > 1 && height > minHeightForWrap;

                    if (shouldWrap) {
                      const lineHeight = fontSize + 2;
                      const maxLines = 2; // Always wrap to maximum 2 lines
                      const numLines = Math.min(maxLines, Math.floor(height / lineHeight));
                      const wordsPerLine = Math.ceil(words.length / numLines);
                      const lines = [];
                      for (let i = 0; i < words.length; i += wordsPerLine) {
                        lines.push(words.slice(i, i + wordsPerLine).join(' '));
                      }

                      return (
                        <text
                          x={x + width / 2}
                          y={y + height / 2 - ((lines.length - 1) * lineHeight) / 2}
                          fill={textColor}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={fontSize}
                          fontWeight="500"
                          style={{ pointerEvents: 'none' }}
                        >
                          {lines.map((line, i) => (
                            <tspan
                              key={i}
                              x={x + width / 2}
                              dy={i === 0 ? 0 : lineHeight}
                            >
                              {line}
                            </tspan>
                          ))}
                        </text>
                      );
                    }

                    return (
                      <text
                        x={x + width / 2}
                        y={y + height / 2}
                        fill={textColor}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={fontSize}
                        fontWeight="500"
                        style={{ pointerEvents: 'none' }}
                      >
                        {displayName}
                      </text>
                    );
                  };

                  // Determine fill color
                  let fillColor;
                  if (isSpacer) {
                    fillColor = 'transparent';
                  } else if (isNetIncrease) {
                    fillColor = '#ABABAB';
                  } else if (isNetDecrease) {
                    fillColor = '#4CAF50';
                  } else {
                    fillColor = categoryColors[actualCategoryId];
                  }

                  // Calculate opacity
                  // If a specific component is active, only that component gets full opacity
                  // If only a category is active (no component), all components in that category get full opacity
                  const isComponentActive = activeComponent === componentId;
                  const isCategoryActive = !activeComponent && activeCategory && actualCategoryId === activeCategory;
                  const isActive = isComponentActive || isCategoryActive;
                  const hasActiveState = activeComponent !== null || activeCategory !== null;
                  const barOpacity = isSpecial ? 1 : (!hasActiveState || isActive ? 1 : 0.3);

                  // Handle mouse interactions
                  const handleMouseEnter = () => {
                    if (isSpecial) return;
                    // Don't change hover if something is locked
                    if (lockedComponent || lockedCategory) return;

                    if (isCategory) {
                      handleCategoryHover(categoryId);
                    } else {
                      handleComponentHover(componentId, actualCategoryId);
                    }
                  };

                  const handleMouseMove = () => {
                    // If we're hovering over a split-out component and it's not already active, activate it
                    if (isSpecial) return;
                    if (lockedComponent || lockedCategory) return;

                    // Only trigger if this is a component (not category) and it's not already the active component
                    if (!isCategory && componentId && activeComponent !== componentId) {
                      handleComponentHover(componentId, actualCategoryId);
                    }
                  };

                  const handleMouseDown = () => {
                    if (isSpecial) return;

                    // Set flag to prevent container click handler from interfering
                    justClickedBarRef.current = true;

                    if (isCategory) {
                      // Clicking a category - lock it
                      if (lockedCategory === categoryId && !lockedComponent) {
                        // Unlock if clicking same category
                        setLockedComponent(null);
                        setLockedCategory(null);
                        setActiveComponent(null);
                        setActiveCategory(null);
                        setActiveInfo(null);
                      } else {
                        setLockedCategory(categoryId);
                        setLockedComponent(null);
                        setActiveCategory(categoryId);
                        setActiveComponent(null);

                        const categoryName = getCategoryDisplayName(categoryId);
                        setActiveInfo({
                          type: 'category',
                          content: `<p>Hover over individual components to see details.</p>`,
                          title: categoryName
                        });
                      }
                    } else {
                      // Clicking a component
                      handleComponentClick(componentId, actualCategoryId);
                    }
                  };

                  return (
                    <Bar
                      key={key}
                      dataKey={key}
                      stackId="a"
                      fill={fillColor}
                      stroke={isSpacer ? 'none' : '#323232'}
                      strokeWidth={0.5}
                      onMouseEnter={handleMouseEnter}
                      onMouseMove={handleMouseMove}
                      onMouseDown={handleMouseDown}
                      opacity={barOpacity}
                      label={renderLabel}
                      isAnimationActive={false}
                      style={{ cursor: isSpecial ? 'default' : 'pointer' }}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend */}
          <div className="chart-legend">
            {Object.entries(categoryColors).map(([categoryId, color]) => (
              <div key={categoryId} className="legend-item">
                <div className="legend-color" style={{ backgroundColor: color }}></div>
                <span className="legend-label" style={{ color: color }}>
                  {categoryId === 'renewables' ? 'Generation Subsidies' :
                   categoryId === 'newLevies' ? 'New Levies' :
                   categoryId.charAt(0).toUpperCase() + categoryId.slice(1)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Right Panes Container */}
        <div className="right-panes">
          {/* Info Pane */}
          <div className="info-pane">
            <div className="info-content">
              {activeInfo ? (
                <>
                  {activeInfo.title && (
                    <h3>
                      {activeInfo.title}
                      {activeInfo.price2025 !== null && activeInfo.price2025 !== undefined &&
                       activeInfo.price2030 !== null && activeInfo.price2030 !== undefined && (
                        <span style={{ display: 'block', fontSize: '16px', fontWeight: '600', color: '#666', marginTop: '8px' }}>
                          2025: <span style={{ color: '#1460F9' }}>£{activeInfo.price2025}</span>
                          {' • '}
                          2030: <span style={{ color: '#1460F9' }}>£{activeInfo.price2030}</span>
                        </span>
                      )}
                    </h3>
                  )}
                  <div dangerouslySetInnerHTML={{ __html: activeInfo.content }} />
                </>
              ) : (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#999',
                  fontSize: '1.1em'
                }}>
                  Hover over chart
                </div>
              )}
            </div>
            {activeInfo && activeInfo.link && (
              <div className="info-link-sticky">
                <a
                  href={activeInfo.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="info-link-button"
                >
                  Assumptions
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '6px' }}>
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    <polyline points="15 3 21 3 21 9" />
                    <line x1="10" y1="14" x2="21" y2="3" />
                  </svg>
                </a>
              </div>
            )}
          </div>

          {/* Wholesale Price Slider - Always visible */}
          <div className="wholesale-slider-pane">
            <h3>Average wholesale price in 2030</h3>
            <div className="slider-container">
              <input
                type="range"
                min="0"
                max="135"
                value={wholesalePrice}
                onChange={(e) => setWholesalePrice(Number(e.target.value))}
                className="wholesale-slider"
              />
              <div className="slider-value">£{wholesalePrice}/MWh</div>
            </div>
            <div className="wholesale-context">
              Recent wholesale price: £84/MWh. Most recent offshore wind: £81/MWh. Cheapest offshore wind: £49/MWh. Hinkley C: £127/MWh.
              <br/>Estimate based on projected capture rates.
            </div>
          </div>

          {/* Scenario Pane - HIDDEN but logic preserved */}
          <div className="scenario-pane" style={{ display: 'none' }}>
            <h3>Live policy discussions</h3>
            <div className="scenario-toggles">
              <div
                className="toggle-item"
                onMouseEnter={() => handleScenarioHover('removeVAT')}
                onMouseLeave={() => handleScenarioHover(null)}
              >
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={scenarioToggles.removeVAT}
                    onChange={() => handleScenarioToggle('removeVAT')}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">Remove VAT</span>
              </div>

              <div
                className="toggle-item"
                onMouseEnter={() => handleScenarioHover('moveECOToTax')}
                onMouseLeave={() => handleScenarioHover(null)}
              >
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={scenarioToggles.moveECOToTax}
                    onChange={() => handleScenarioToggle('moveECOToTax')}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">Move ECO to tax</span>
              </div>

              <div
                className="toggle-item"
                onMouseEnter={() => handleScenarioHover('reindexROandFiT')}
                onMouseLeave={() => handleScenarioHover(null)}
              >
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={scenarioToggles.reindexROandFiT}
                    onChange={() => handleScenarioToggle('reindexROandFiT')}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">Re-index RO & FiT</span>
              </div>
            </div>

            {/* Divider */}
            <div className="scenario-divider"></div>

            {/* Bottom toggles row */}
            <div className="bottom-toggles">
              {/* What to do? Info */}
              <div
                className="necessary-changes-info"
                onMouseEnter={() => setShowSolutionsModal(true)}
                onMouseLeave={() => setShowSolutionsModal(false)}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <span>What to do?</span>

                {showSolutionsModal && (
                  <div className="solutions-modal">
                    <p>We can have a cheaper system by 2030, but we'll need to take costs seriously.</p>
                    <ul>
                      <li><strong>Spend efficiently on grid infrastructure. </strong>
                      In December, OFGEM is due to approve the largest spending increase in the history of privatised electricity transmission. The asset value of some network companies will ~double by 2030, and consumers get billed a percentage of this asset value. This will remain on bills for decades, since power grid assets get depreciated (charged to consumers) over 45 years. Cost discipline is essential, as well as full consideration of alternatives. Co-located batteries and flexibility must be given the right incentives to help curtailment and constraints. Locational signals are important here.</li>
                      <li><strong>No new dead-end levies.</strong> This chart does not contain the planned funding for Carbon Capture and Hydrogen-to-Power projects. Unfortunately it is structurally impossible for these to lower bills, being both uncompetitive as power sources (£/MWh) and abatement solutions (£/tCO2). </li>
                      <li><strong>Wholesale market adjustments.</strong> Carbon pricing is good, but the carbon price on gas fired generation is discouraging electrification. Reducing this carbon price would not materially change the amount of gas being burned, but would save consumers £billions per year and improve the economics of EVs, heat pumps and data centers.</li>
                    </ul>
                  </div>
                )}
              </div>

              {/* Adjust wholesale price toggle */}
              <div className="toggle-item">
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showWholesaleSlider}
                    onChange={() => setShowWholesaleSlider(!showWholesaleSlider)}
                  />
                  <span className="toggle-slider"></span>
                </label>
                <span className="toggle-label">Adjust wholesale</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="forecast2030-footer">
        <p>
          By <a href="https://benjames.io" target="_blank" rel="noopener noreferrer">Ben James</a>.
          This is an estimate. <a href="https://docs.google.com/spreadsheets/d/1fhddDhderRQIxb0gzrJZKpOa2UgD9ZlrgWD58du-qbE/edit?gid=1889739130#gid=1889739130" target="_blank" rel="noopener noreferrer">
            Data and assumptions
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '4px', verticalAlign: 'middle' }}>
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
          </a>
        </p>
        <p>
          Email me: <a href={`mailto:${email}`}>{email}</a>. I'm interested in solutions!
        </p>
      </div>
    </div>
  );
};

export default Forecast2030;
